import type { HeatShift } from '@reditelji/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { ChangelogEntry, EventDay, ShiftConflict } from './types';

export interface OverviewDayStats {
  pct: number;
  winMin: number;
  gapMin: number;
  gapCount: number;
  nConf: number;
  nPend: number;
  /** Shifts an external team covers — outside the confirmed/pending split. */
  nExternal: number;
  nOpen: number;
  rejPeople: number;
  pendPeople: number;
}

export interface OverviewDay extends EventDay {
  stats: OverviewDayStats;
}

/** One post on one day: what the Pokritost po urah heatmap is drawn from. */
export interface OverviewGridCell {
  winMin: number;
  gapMin: number;
  /** Uncovered stretches of 15 min or more — the gap feed's own. */
  gaps: { fromMin: number; toMin: number }[];
  windows: { fromMin: number; toMin: number }[];
  /** Every staffed shift, with how settled it is. */
  shifts: HeatShift[];
}

export interface OverviewGridRow {
  postId: string;
  code: string;
  micro: string;
  urgent: boolean;
  sectorId: string;
  sectorName: string;
  cells: Record<string, OverviewGridCell>;
}

export interface RejectionBlocker {
  volunteerId: string;
  name: string;
  /** The shift the row is about; `null` for a refusal naming none they still hold. */
  refused: { postCode: string; fromMin: number; toMin: number; status: 'cant' | 'change' } | null;
  /** `''` for a login not shown the words (SPEC §1.1L). */
  comment: string;
  at: string | null;
  eventDayId: string;
  shiftId: string;
}

export interface GapBlocker {
  eventDayId: string;
  postId: string;
  postCode: string;
  micro: string;
  sectorName: string;
  urgent: boolean;
  fromMin: number;
  toMin: number;
}

export interface ConflictBlocker extends ShiftConflict {
  name: string;
  postCodes: [string, string];
  fromMin: number;
  toMin: number;
}

export interface ReminderBlocker {
  volunteerId: string;
  name: string;
  hasEmail: boolean;
  briefingSentAt: string | null;
  briefingSentVersion: number | null;
  scheduleVersion: number;
  waitingDays: number | null;
  shiftCount: number;
}

export interface LongDayBlocker {
  volunteerId: string;
  eventDayId: string;
  totalMin: number;
  name: string;
  /** Set for an external team's place: the row opens the team, not a profile. */
  externalGroupId: string | null;
}

export interface OverviewPayload {
  days: OverviewDay[];
  grid: OverviewGridRow[];
  blockers: {
    rejections: RejectionBlocker[];
    gaps: GapBlocker[];
    conflicts: ConflictBlocker[];
    longDays: LongDayBlocker[];
    reminders: ReminderBlocker[];
    /** Organizacija threads this viewer owes: a question for the team, a reply for the organisation. */
    notes: { volunteerId: string; name: string; since: string; kind: 'question' | 'reply' }[];
  };
  people: {
    /**
     * Owe an answer to the schedule they were sent. Optional, like the two
     * below: an API rolled back past them answers without, as `noResponse`.
     */
    notConfirmed?: { id: string; name: string; totalMin: number }[];
    noResponse?: { id: string; name: string; totalMin: number }[];
    /** Our question has gone unanswered past the threshold (`commsTurn`). */
    noReply?: { id: string; name: string; days: number; since: string; messageId: string; totalMin: number }[];
    /** Wrote to us, and nobody has answered yet. */
    awaitingUs?: { id: string; name: string; days: number; since: string; messageId: string; totalMin: number }[];
    overloaded: { id: string; name: string; days: { eventDayId: string; minutes: number }[] }[];
    unused: { id: string; name: string }[];
    /** Withdrew from the event (SPEC §1.1K); never in the three above. */
    cancelled: { id: string; name: string; cancelledAt: string }[];
  };
  /** People whose briefing page differs from the current plan (SPEC §1.1F). */
  publication?: { stale: number; never: number };
  recentChanges: ChangelogEntry[];
  renotifyCount?: number;
}

export function useOverview() {
  return useQuery({
    queryKey: ['overview'],
    queryFn: () => api<OverviewPayload>('/overview'),
    placeholderData: (prev) => prev,
  });
}
